<template>
  <DropdownMenu v-if="data?.teams && profile">
    <DropdownMenuTrigger class="group/trigger">
      <div class="flex h-9 max-w-[200px] items-center px-1 select-none">
        <Avatar class="size-9">
          <AvatarImage :src="profile.gravatar_url" alt="Profile picture"></AvatarImage>
          <AvatarFallback>{{ profile.first_name[0] }} {{ profile.last_name[0] }}</AvatarFallback>
        </Avatar>
        <ChevronsUpDownIcon class="ml-1 h-4 w-4 shrink-0" />
      </div>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-60 p-1" align="end">
      <div class="p-2">
        <h4 class="truncate text-sm font-medium">
          {{ profile.first_name }} {{ profile.last_name }}
        </h4>
        <p class="text-muted-foreground truncate text-xs">{{ profile.email }}</p>
        <div v-if="profile.limits" class="mt-3 space-y-1.5">
          <div class="flex items-center justify-between">
            <span class="text-muted-foreground text-xs font-medium">Account limits</span>
            <span class="text-xs font-medium">
              {{ profile.limits.stacklets.used }}/{{ profile.limits.stacklets.available }}
            </span>
          </div>
          <div class="bg-secondary h-1.5 overflow-hidden rounded-full">
            <div
              :class="
                cn('h-1.5', {
                  'bg-primary': stackletsUsagePercent < 80,
                  'bg-yellow-500': stackletsUsagePercent >= 80 && stackletsUsagePercent < 95,
                  'bg-red-500': stackletsUsagePercent >= 95,
                })
              "
              :style="{
                width: `${stackletsUsagePercent}%`,
              }"
            ></div>
          </div>
          <p class="text-muted-foreground text-xs">Stacklets</p>
        </div>
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem as-child>
          <RouterLink :to="{ name: 'profile' }">
            <UserIcon class="mr-2 h-4 w-4" />
            Profile
          </RouterLink>
        </DropdownMenuItem>
        <DropdownMenuItem v-if="IS_BILLING_ENABLED ? (team ? isOwner : true) : false" as-child>
          <RouterLink :to="{ name: 'billing' }">
            <CreditCardIcon class="mr-2 h-4 w-4" />
            Billing
          </RouterLink>
        </DropdownMenuItem>
        <DropdownMenuItem as-child>
          <RouterLink :to="{ name: 'profile/notifications' }">
            <BellDotIcon class="mr-2 h-4 w-4" />
            Notifications
          </RouterLink>
        </DropdownMenuItem>
        <DropdownMenuItem as-child>
          <RouterLink :to="{ name: 'profile/logs' }">
            <HistoryIcon class="mr-2 h-4 w-4" />
            Logs
          </RouterLink>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem @click="handleLogout">
        <LogOutIcon class="mr-2 h-4 w-4" />
        <span>Log out</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
  <div v-else class="flex items-center gap-1">
    <div class="bg-muted size-9 rounded-full"></div>
    <ChevronsUpDownIcon class="h-4 w-4 shrink-0" />
  </div>
</template>

<script setup lang="ts">
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  BellDotIcon,
  ChevronsUpDownIcon,
  CreditCardIcon,
  HistoryIcon,
  LogOutIcon,
  UserIcon,
} from 'lucide-vue-next';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { logout, profileQueryOptions } from '@/entities/profile';
import { teamQueryOptions, teamsQueryOptions } from '@/entities/team';
import { IS_BILLING_ENABLED } from '@/shared/config';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { cn } from '@/shared/lib/misc';

const router = useRouter();
const queryClient = useQueryClient();
const { data: profile } = useQuery(profileQueryOptions());
const { data } = useQuery(teamsQueryOptions());
const { currentTeamUuid, clearTeamCookie } = useTeamCookie();

const { data: team } = useQuery(teamQueryOptions(currentTeamUuid));

const isOwner = computed(() => {
  const owner = team.value?.members.find((member) => member.role === 'Owner');
  return !!owner && owner.uuid === profile.value?.uuid;
});

const stackletsUsagePercent = computed(() => {
  if (!profile.value?.limits) return 0;
  return Math.min(
    (profile.value.limits.stacklets.used / profile.value.limits.stacklets.available) * 100,
    100,
  );
});

async function handleLogout() {
  await logout();
  window.localStorage.clear();
  queryClient.clear();
  clearTeamCookie();
  router.push({ name: 'login', query: { logout: 'success' } }).catch(() => {});
}
</script>
